import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { appStore } from "entities/App";
import { searchFilterStore } from "entities/SearchFilter";
import { useWindowSize } from "shared/hooks/useWindowSize.hook";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { useTranslation } from "shared/localization/localization";
import { /* AnalyticsIcon, CalendarIcon, NotificationsIcon, */ MapIcon, ShipmentsIcon } from "shared/ui/Icons";
// import { Analytics } from "./Analytics";
// import { Calendar } from "./Calendar";
// import { Notifications } from "./Notifications";
import s from "./ToolsTabs.module.scss";

// Tabs bar is only for signed-in users on narrow screens (CSS hides it from 768px).
const TOOLS_TABS_VISIBLE_BELOW = 768;

enum Tab {
  Shipments = "shipments",
  Map = "map",
  Analytics = "analytics",
  Notifications = "notifications",
  Calendar = "calendar",
}

type TabConfig = {
  id: Tab;
  icon: ReactNode;
  translationKey: string;
  content: ReactNode;
  mobileOnly?: boolean; // Show only on mobile devices
  panelVisible?: boolean; // Control panel visibility on mobile
};

const SEA_TAB_CONFIGS: TabConfig[] = [
  {
    id: Tab.Shipments,
    icon: <ShipmentsIcon />,
    translationKey: "Shipments",
    content: null,
    mobileOnly: true,
    panelVisible: true,
  },
  {
    id: Tab.Map,
    icon: <MapIcon />,
    translationKey: "Map",
    content: null,
    panelVisible: false,
  },
  // {
  //   id: Tab.Analytics,
  //   icon: <AnalyticsIcon />,
  //   translationKey: "Analytics",
  //   content: <Analytics />,
  // },
  // {
  //   id: Tab.Notifications,
  //   icon: <NotificationsIcon />,
  //   translationKey: "Notifications",
  //   content: <Notifications />,
  // },
  // {
  //   id: Tab.Calendar,
  //   icon: <CalendarIcon />,
  //   translationKey: "Calendar",
  //   content: <Calendar />,
  // },
];

const AIR_TAB_CONFIGS: TabConfig[] = [
  {
    id: Tab.Shipments,
    icon: <ShipmentsIcon />,
    translationKey: "Shipments",
    content: null,
    mobileOnly: true,
    panelVisible: true,
  },
  {
    id: Tab.Map,
    icon: <MapIcon />,
    translationKey: "Map",
    content: null,
    panelVisible: false,
  },
];

const RAIL_TAB_CONFIGS: TabConfig[] = [
  {
    id: Tab.Shipments,
    icon: <ShipmentsIcon />,
    translationKey: "Shipments",
    content: null,
    mobileOnly: true,
    panelVisible: true,
  },
  {
    id: Tab.Map,
    icon: <MapIcon />,
    translationKey: "Map",
    content: null,
    panelVisible: false,
  },
];

const ROAD_TAB_CONFIGS: TabConfig[] = [
  {
    id: Tab.Shipments,
    icon: <ShipmentsIcon />,
    translationKey: "Shipments",
    content: null,
    mobileOnly: true,
    panelVisible: true,
  },
  {
    id: Tab.Map,
    icon: <MapIcon />,
    translationKey: "Map",
    content: null,
    panelVisible: false,
  },
];

const PARCEL_TAB_CONFIGS: TabConfig[] = [
  {
    id: Tab.Shipments,
    icon: <ShipmentsIcon />,
    translationKey: "Shipments",
    content: null,
    mobileOnly: true,
    panelVisible: true,
  },
  {
    id: Tab.Map,
    icon: <MapIcon />,
    translationKey: "Map",
    content: null,
    panelVisible: false,
  },
];

type ToolsTabsProps = {
  className?: string;
  toolbarStart?: ReactNode;
};

export const ToolsTabs: RFC<ToolsTabsProps> = ({ className, toolbarStart, children }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isPanelVisible = useAppSelector(appStore.selectors.isPanelVisible);
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const filler = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<Tab>(Tab.Map);

  const { width } = useWindowSize({ debounceTime: 70 });

  // Determine if device is mobile based on width
  const isMobile = useMemo(() => (width ? width < TOOLS_TABS_VISIBLE_BELOW : false), [width]);

  // Get tab configuration based on current shipment type
  const currentTabConfigs = useMemo(() => {
    switch (shipment) {
      case ShipmentType.SEA:
        return SEA_TAB_CONFIGS;
      case ShipmentType.AIR:
        return AIR_TAB_CONFIGS;
      case ShipmentType.RAIL:
        return RAIL_TAB_CONFIGS;
      case ShipmentType.ROAD:
        return ROAD_TAB_CONFIGS;
      case ShipmentType.PARCEL:
        return PARCEL_TAB_CONFIGS;
      default:
        return SEA_TAB_CONFIGS;
    }
  }, [shipment]);

  // Filter visible tabs based on mobile/desktop view
  const visibleTabs = useMemo(
    () => currentTabConfigs.filter((tab) => !tab.mobileOnly || isMobile),
    [currentTabConfigs, isMobile]
  );

  // Get active tab configuration
  const activeTabConfig = useMemo(
    () => currentTabConfigs.find((tab) => tab.id === activeTab),
    [currentTabConfigs, activeTab]
  );

  // Handle filler position based on panel visibility
  useEffect(() => {
    if (filler.current) {
      filler.current.style.left = isPanelVisible ? "0" : "50%";
    }
  }, [isPanelVisible]);

  // Handle responsive tab switching when screen size or shipment type changes
  useEffect(() => {
    if (!width) return;

    if (isMobile) {
      setActiveTab(Tab.Shipments);
    } else {
      dispatch(appStore.actions.setIsPanelVisible(true));
      setActiveTab(Tab.Map);
    }
  }, [isMobile, width, shipment, dispatch]);

  // Handle tab switching
  const handleTabSwitch = (tabId: Tab): void => {
    setActiveTab(tabId);

    // Handle mobile panel visibility
    if (isMobile) {
      const tabConfig = currentTabConfigs.find((tab) => tab.id === tabId);
      if (tabConfig?.panelVisible !== undefined) {
        dispatch(appStore.actions.setIsPanelVisible(tabConfig.panelVisible));
      }
    }
  };

  return (
    <>
      <div className={cn(s.toolbar, className)}>
        <div className={s.toolsTabsWrapper}>
          <ul className={s.tabsList}>
            {visibleTabs.map((tabConfig) => (
              <li
                key={tabConfig.id}
                data-test-id={`tools-tab-${tabConfig.translationKey.toLowerCase()}-button`}
                className={cn({ [s.active]: activeTab === tabConfig.id })}
                onClick={() => handleTabSwitch(tabConfig.id)}
              >
                {tabConfig.icon}
                <span>{t(tabConfig.translationKey)}</span>
              </li>
            ))}
          </ul>
          {children}
        </div>
        {toolbarStart && <div className={s.toolbarStart}>{toolbarStart}</div>}
      </div>
      <div className={cn(s.contentWrapper, { [s.hidden]: !activeTabConfig?.content })}>{activeTabConfig?.content}</div>
      <div ref={filler} className={s.filler} />
    </>
  );
};
